import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import {
  Box,
  Button,
  IconButton,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import type { LegacyRef } from 'react';
import { forwardRef, useRef, useState } from 'react';

interface FilePickerProps {
  filehandler: (value: File | null) => void;
  namehandler?: (value: string) => void;
}

function FilePicker(
  { filehandler, namehandler }: FilePickerProps,
  ref: LegacyRef<HTMLDivElement>
): JSX.Element {
  const [file, setFile] = useState<File | null>();
  const inputFile = useRef<HTMLInputElement>(null);

  const handleReset = () => {
    if (inputFile.current) {
      inputFile.current.value = '';
    }
  };

  const addFile = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files) {
      const selectedFile = Array.from(e.target.files)[0];
      filehandler(selectedFile as File);
      setFile(selectedFile);
    }
  };

  const clearFile = () => {
    setFile(null);
    handleReset();
    filehandler(null);
  };

  return (
    <Box ref={ref as React.Ref<HTMLDivElement>}>
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          gap: 1,
          border: '1px solid',
          borderColor: 'divider',
          borderRadius: 1,
          p: 1,
        }}
      >
        <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
          {namehandler && (
            <TextField
              size="small"
              placeholder="Nom de la ressource"
              aria-label="Nom de la ressource"
              onChange={(e) => namehandler(e.target.value)}
              sx={{ flex: '1 1 180px', minWidth: 180 }}
            />
          )}
          <Button
            variant="outlined"
            component="label"
            size="small"
            sx={{ flexShrink: 0 }}
          >
            Parcourir...
            <input
              ref={inputFile}
              type="file"
              hidden
              aria-label="Sélectionner un fichier"
              onChange={addFile}
            />
          </Button>
          {file && (
            <Typography variant="body2" color="text.secondary" noWrap>
              {file.name.length < 20
                ? file.name
                : `${file.name.substring(0, 17)}...`}
            </Typography>
          )}
        </Stack>
      </Box>
      {file && (
        <Box
          sx={{
            mt: 2,
            border: '1px solid',
            borderColor: 'divider',
            borderRadius: 1,
            overflow: 'hidden',
          }}
        >
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', p: 0.5 }}>
            <IconButton
              aria-label="Supprimer le fichier"
              size="small"
              onClick={clearFile}
              color="error"
            >
              <DeleteOutlineIcon fontSize="small" />
            </IconButton>
          </Box>
          <Box sx={{ textAlign: 'center', px: 2, pb: 2 }}>
            <Box
              component="img"
              src={URL.createObjectURL(file)}
              alt="Aperçu du fichier sélectionné"
              sx={{
                maxWidth: '100%',
                maxHeight: 200,
                objectFit: 'contain',
              }}
            />
          </Box>
        </Box>
      )}
    </Box>
  );
}

export default forwardRef(FilePicker);
